import {Box, IconButton, Stack, Typography} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowLeft01Icon} from "@hugeicons/core-free-icons";
import {ProjectCreateFormNew} from "./components/ProjectCreateFormNew";
import {newSubProjectRow, subProjectRowName} from "./components/ProjectFormNew";
import {toApiConfiguration, toVoiceGemini} from "./components/LanguageConfigurationsNew";
import axios from "../../config/axios";
import {toast} from "react-toastify";
import {OrganizationRepository} from "../../repositories/OrganizationRepository";
import {SubProjectRepository, ProjectRepository} from "../../repositories/ProjectRepository";
import {SignInContext} from "../../context/SignInContext";
import {useCatalog} from "../../context/CatalogContext";
import {ProjectEntity} from "../../model/ModelData";
import {Context as AppContext, ProjectRoles} from "../../context/AppContext";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {WebSettingsContext} from "../../context/WebSettingsContext";
import {projectPath} from "../registry-project/RegistryProjectRoute";
import CreateProjectTour from "../../components/tour/CreateProjectTour";
import {deleteDoc, doc} from "firebase/firestore";
import {db} from "../../config/firebase";
import {PROJECTS_COLLECTION} from "../../db/collections";
import {subProjectLabel} from "../../utils/subProjects";

export const ProjectCreationPageNew = () => {
  const {currentUser} = useContext(SignInContext);
  const {searchParams} = useContext(WebSettingsContext);
  const {createProject, getUserProjects} = useContext(AppContext);
  const {items: languages} = useCatalog("languages");
  const {items: voiceModels} = useCatalog("myVoiceModels");
  const [organizations, setOrganizations] = useState([]);
  const [project, setProject] = useState({
    organization: searchParams?.organization,
    status: "BUILDING_SCENARIOS",
    projectType: "VOICE_BOT",
    mode: "stage",
    subProjects: [newSubProjectRow()],
  });
  const [loading, setLoading] = useState(false);
  const [runCreateProjectTour] = useState(() => {
    return sessionStorage.getItem("create_project_tour") === "true";
  });
  const navigate = useNavigate();
  const [queryParams] = useSearchParams()

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (runCreateProjectTour) {
      sessionStorage.removeItem("create_project_tour");
    }
  }, [runCreateProjectTour]);

  const fetchData = async () => {
    try {
      const organizationResponse = await OrganizationRepository.getMyOrganizations();
      setOrganizations(organizationResponse?.data || []);
    } catch (err) {
      toast.error(err.message);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!project?.name || !project?.language) {
      toast.error("All fields are required!");
      return;
    }
    if (project?.file) {
      importAssistant();
      return;
    }
    const rows = project?.subProjects || [];
    if (rows.some(row => !row.languageId)) {
      toast.error("Every sub project needs a language");
      return;
    }
    // names must be unique within the project (case-insensitive); a blank name becomes the language's name
    const names = rows.map(row => subProjectRowName(row, languages).toLowerCase());
    if (new Set(names).size !== names.length) {
      toast.error("Sub project names must be unique. Give sub projects with the same language their own names.");
      return;
    }
    createNewAssistant();
  }

  // Firebase flow for one sub project; its id is the flowUid generated by the backend
  // config: the sub project's voice / transcribe configuration from the form
  const buildFirebaseProject = (subProject, org, name, config, projectId) => {
    const language = languages.find(item => item.id === subProject.languageId);
    // deep copy: ProjectEntity is shared, and every language gets its own settings
    const formData = {
      ...JSON.parse(JSON.stringify(ProjectEntity)),
      id: subProject.flowUid,
      name,
      description: project?.description || "",
      is_demo: false,
      createdAt: new Date().getTime(),
      status: subProject.status || project?.status || "BUILDING_SCENARIOS",
    }
    formData.settings.organization = project?.organization || null;
    formData.settings.language = subProject.languageId;
    formData.settings.lang = language?.lang || null;
    formData.settings.organization_type = org?.organization_type || "";
    formData.settings.speechToText = project?.speechToText || null;
    formData.settings.voiceGemini = toVoiceGemini(config, voiceModels);
    formData.settings.projectType = project?.projectType || "VOICE_BOT";
    formData.settings.mode = project?.mode || "stage";
    formData.settings.category = queryParams.get('category') || null;
    formData.settings.project_id = projectId;
    formData.settings.subproject_id = subProject.id;
    formData.team = org?.members?.map(i => i?.member) || [];
    formData.members = org?.members?.map(i => ({username: i?.member, role: currentUser?.username === i?.member ? ProjectRoles.ADMIN : ProjectRoles.AGENT, vdn: ""})) || [];
    if (!formData.team.includes(currentUser?.username)) {
      formData.team.push(currentUser?.username);
      formData.members.push({username: currentUser?.username, role: ProjectRoles.ADMIN, vdn: ""})
    }
    return formData;
  }

  // Creates the project in the backend, then one Firebase flow per returned sub project
  const createNewAssistant = async () => {
    setLoading(true);
    let createdProject = null;
    const createdFlowUids = [];
    try {
      const org = organizations?.find(org => org?.id === (project?.organization || project?.organization_id || project?.settings?.organization));
      const rows = project?.subProjects || [];

      const response = await ProjectRepository.create({
        name: project?.name || "",
        description: project?.description || "",
        organizationId: project?.organization || null,
        backendConfigurationId: project?.backendConfigurationId || null,
        // a blank name is left out so the backend defaults it to the language's name
        subProjects: rows.map(row => ({
          name: (row.name || "").trim() || null,
          description: (row.description || "").trim() || null,
          languageId: row.languageId,
        })),
        type: project?.projectType === "CHAT_BOT" ? "CHATBOT" : "VOICE_ASSISTANT",
      });
      createdProject = response.data;

      const subProjects = createdProject?.subProjects || [];
      if (!subProjects.length) {
        throw new Error("The project was created without sub projects");
      }
      // created sub projects are matched to their form rows by name (unique within the project);
      // several can share a language, so the language alone does not identify them
      const rowOf = (subProject) => rows.find(row =>
        subProjectRowName(row, languages).toLowerCase() === (subProject.name || "").toLowerCase());
      // the creation endpoint takes no configuration, so each sub project is configured afterwards
      for (const subProject of subProjects) {
        const config = project?.languageConfigs?.[rowOf(subProject)?.key];
        if (config) {
          await SubProjectRepository.edit(subProject.id, toApiConfiguration(config));
        }
      }

      for (const subProject of subProjects) {
        const name = subProjects.length > 1
          ? `${project?.name || ""} (${subProjectLabel(subProject)})`
          : project?.name || "";
        await createProject(buildFirebaseProject(subProject, org, name, project?.languageConfigs?.[rowOf(subProject)?.key], createdProject.id));
        createdFlowUids.push(subProject.flowUid);
      }

      toast.success("Successfully created project.");
      getUserProjects();
      navigate(projectPath(createdProject.id, subProjects[0].id));
    } catch (e) {
      console.error(e);
      // roll back so the backend project and its flows are created together or not at all
      await Promise.all(createdFlowUids.map(flowUid =>
        deleteDoc(doc(db, PROJECTS_COLLECTION, flowUid)).catch(error => console.error(error))
      ));
      if (createdProject?.id) {
        await ProjectRepository.delete(createdProject.id).catch(error => console.error(error));
      }
      toast.error(e?.response?.data?.message || e?.message || "Failed to create project");
    } finally {
      setLoading(false);
    }
  }

  const importAssistant = async () => {
    if (!project?.file) {
      toast.error("All fields are required!");
      return;
    }

    try {
      setLoading(true);
      const org = organizations?.find(org => org?.id === (project?.organization || project?.organization_id || project?.settings?.organization));
      // Create a FormData object
      const formData = new FormData();

      // Append the file to the FormData object
      formData.append('file', project?.file); // Replace 'file' with the actual file input
      formData.append('username', currentUser?.username);
      formData.append('name', project?.name);
      formData.append('description', project?.description || "");
      formData.append('is_demo', 'false');
      formData.append('organization', project?.organization);
      formData.append('organization_type', org?.organization_type || "");
      formData.append('speechToText', project?.speechToText);
      formData.append('language', project?.language);
      formData.append('lang', project?.lang);
      formData.append('category',      queryParams.get('category') || null);

      const response = await axios.post(
        `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/importProject`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': "*"
          }
        });
      console.log('File uploaded successfully:', response.data);
      await getUserProjects();
      navigate(response?.data?.id ? `/project/${response?.data?.id}/flow` : "/projects")

      toast.success("Project imported successfully!")
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error("Failed to import project!")
    } finally {
      setLoading(false);
    }

  }
  return (
    <>
      <CreateProjectTour run={runCreateProjectTour}/>
      <Box sx={{maxWidth: "lg", mx: "auto", px: {xs: 2, md: 3}, py: {xs: 3, md: 4}}}>
        <Stack direction={"row"} alignItems={"center"} gap={1.25} sx={{mb: 2}}>
          <IconButton component={Link} to={"/projects"} sx={{color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}}}>
            <HugeiconsIcon icon={ArrowLeft01Icon} size={20}/>
          </IconButton>
          <Box>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>PROJECTS</Typography>
            <Typography component={"h1"} sx={{fontSize: 22, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
              Create Project
            </Typography>
          </Box>
        </Stack>

        <Box
          data-tour={"create-project-form"} component={"form"} onSubmit={handleSubmit}
          sx={{bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "18px", boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)"}}
        >
          <ProjectCreateFormNew
            project={project} setProject={setProject}
            languages={languages} organizations={organizations}
            fetchData={fetchData}
            loading={loading}
          />
        </Box>
      </Box>
    </>
  )
}
